<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Statement of Financial Position (Balance Sheet)
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <!-- Date Filters -->
                <v-row class="py-2">
                  <v-col md="3" cols="12">
                    <v-date-input 
                      v-model="filters.from_date" 
                      label="From Date" 
                      density="compact" 
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-start" 
                      variant="outlined" 
                      bg-color="grey-lighten-4" 
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col md="3" cols="12">
                    <v-date-input 
                      v-model="filters.to_date" 
                      label="To Date" 
                      density="compact" 
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-end" 
                      variant="outlined" 
                      bg-color="grey-lighten-4" 
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col md="6" cols="12">
                    <div class="d-flex justify-start align-center" style="gap: 12px;">
                      <v-btn class="bg-brand w-15" type="submit" @click="loadBalanceSheet">
                        <v-icon left>mdi-magnify</v-icon>
                        Generate Report
                      </v-btn>
                      <v-btn class="bg-warning w-15" type="button" prepend-icon="mdi-refresh" @click="resetFilters">
                        Reset
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <!-- Export Buttons -->
                <v-row v-if="balanceSheetData" class="py-2 mt-2">
                  <v-col cols="12">
                    <div class="d-flex justify-center">
                      <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success" @click="exportToExcel" :disabled="!balanceSheetData">EXCEL</v-btn>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red" @click="exportToPDF" :disabled="!balanceSheetData">PDF</v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>
                </v-row>

                <!-- Balance Sheet Report -->
                <v-card v-if="balanceSheetData" class="mt-2">
                  <v-card-title class="bg-primary text-white text-center">
                    <div class="w-100">
                      <div class="text-h5 font-weight-bold">STATEMENT OF FINANCIAL POSITION</div>
                      <div class="text-subtitle-1">FOR THE AS AT {{ formatDate(filters.to_date) }}</div>
                    </div>
                  </v-card-title>

                  <v-card-text class="pa-0">
                    <!-- Balance Sheet Table - Exact Image Design -->
                    <div class="balance-sheet-exact">
                      <!-- Header -->
                      <div class="balance-header">
                        <div class="header-particulars">Particulars</div>
                        <div class="header-amounts">Amount in Taka</div>
                      </div>
                      <div class="balance-subheader">
                        <div class="header-particulars"></div>
                        <div class="header-amount-box">0.00</div>
                        <div class="header-amount-box">0.00</div>
                      </div>

                      <!-- 1. Current Assets Section -->
                      <div class="balance-row">
                        <div class="particulars-text section-header">1. CURRENT ASSETS</div>
                        <div class="amount-box-empty"></div>
                        <div class="amount-box-empty"></div>
                      </div>

                      <!-- Current Assets Items -->
                      <template v-if="balanceSheetData.assets && balanceSheetData.assets[0]">
                        <div v-for="(item, index) in balanceSheetData.assets[0].account_head_groups" :key="'current-' + index" class="balance-row">
                          <div class="particulars-text indent">{{ item.name }}</div>
                          <div class="amount-box">{{ item.current_balance }}</div>
                          <div class="amount-box">{{ item.previous_balance }}</div>
                        </div>
                      </template>

                      <!-- 2. Fixed Assets Section -->
                      <div class="balance-row">
                        <div class="particulars-text section-header">2. FIXED ASSETS</div>
                        <div class="amount-box-empty"></div>
                        <div class="amount-box-empty"></div>
                      </div>

                      <!-- Fixed Assets Items -->
                      <template v-if="balanceSheetData.assets && balanceSheetData.assets[1]">
                        <div v-for="(item, index) in balanceSheetData.assets[1].account_head_groups" :key="'fixed-' + index" class="balance-row">
                          <div class="particulars-text indent">{{ item.name }}</div>
                          <div class="amount-box">{{ item.current_balance }}</div>
                          <div class="amount-box">{{ item.previous_balance }}</div>
                        </div>
                      </template>

                      <!-- Total Assets -->
                      <div class="balance-row total-row">
                        <div class="particulars-text total-text">Total Assets (1+2)</div>
                        <div class="amount-box total-box">{{ balanceSheetData.assets_total?.current_balance }}</div>
                        <div class="amount-box total-box">{{ balanceSheetData.assets_total?.previous_balance }}</div>
                      </div>

                      <!-- Separator Line -->
                      <div class="separator-line"></div>

                      <!-- 3. Equity Section -->
                      <div class="balance-row">
                        <div class="particulars-text section-header">3. EQUITY</div>
                        <div class="amount-box-empty"></div>
                        <div class="amount-box-empty"></div>
                      </div>

                      <!-- Equity Items -->
                      <template v-if="balanceSheetData.equities">
                        <div v-for="(item, index) in balanceSheetData.equities" :key="'equity-' + index" class="balance-row">
                          <div class="particulars-text indent">{{ item.name }}</div>
                          <div class="amount-box">{{ item.current_balance }}</div>
                          <div class="amount-box">{{ item.previous_balance }}</div>
                        </div>
                      </template>

                      <!-- 4. Current Liabilities Section -->
                      <div class="balance-row">
                        <div class="particulars-text section-header">4. CURRENT LIABILITIES</div>
                        <div class="amount-box-empty"></div>
                        <div class="amount-box-empty"></div>
                      </div>

                      <!-- Current Liabilities Items -->
                      <template v-if="balanceSheetData.liabilities && balanceSheetData.liabilities[0]">
                        <div v-for="(item, index) in balanceSheetData.liabilities[0].account_head_groups" :key="'current-liab-' + index" class="balance-row">
                          <div class="particulars-text indent">{{ item.name }}</div>
                          <div class="amount-box">{{ item.current_balance }}</div>
                          <div class="amount-box">{{ item.previous_balance }}</div>
                        </div>
                      </template>

                      <!-- 5. Non-Current Liabilities Section -->
                      <div class="balance-row">
                        <div class="particulars-text section-header">5. NON CURRENT LIABILITIES</div>
                        <div class="amount-box-empty"></div>
                        <div class="amount-box-empty"></div>
                      </div>

                      <!-- Non-Current Liabilities Items -->
                      <template v-if="balanceSheetData.liabilities && balanceSheetData.liabilities[1]">
                        <div v-for="(item, index) in balanceSheetData.liabilities[1].account_head_groups" :key="'non-current-liab-' + index" class="balance-row">
                          <div class="particulars-text indent">{{ item.name }}</div>
                          <div class="amount-box">{{ item.current_balance }}</div>
                          <div class="amount-box">{{ item.previous_balance }}</div>
                        </div>
                      </template>

                      <!-- Total Equity and Liabilities -->
                      <div class="balance-row grand-total-row">
                        <div class="particulars-text grand-total-text">Total Equity and Liabilities (3+4+5)</div>
                        <div class="amount-box grand-total-box">{{ balanceSheetData.equities_and_liabilities_total?.current_balance }}</div>
                        <div class="amount-box grand-total-box">{{ balanceSheetData.equities_and_liabilities_total?.previous_balance }}</div>
                      </div>
                    </div>

                    <!-- Balance Status -->
                    <!-- <v-card-text class="bg-grey-lighten-5">
                      <v-alert 
                        :type="balanceSheetData.additional_message?.includes('balanced') ? 'success' : 'creation'" 
                        variant="tonal"
                        class="mb-0"
                      >
                        <v-icon left>{{ balanceSheetData.additional_message?.includes('balanced') ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
                        {{ balanceSheetData.additional_message || 'Balance sheet generated successfully' }}
                      </v-alert>
                    </v-card-text> -->

                  </v-card-text>
                </v-card>

                <!-- Loading State -->
                <v-card v-if="loading" class="mt-4">
                  <v-card-text class="text-center py-8">
                    <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                    <p class="mt-4 text-h6">Generating Balance Sheet...</p>
                  </v-card-text>
                </v-card>

                <!-- Error State -->
                <v-card v-if="error" class="mt-4">
                  <v-card-text class="text-center py-8">
                    <v-icon color="error" size="64">mdi-alert-circle</v-icon>
                    <p class="mt-4 text-h6 text-error">{{ error }}</p>
                    <v-btn color="primary" @click="loadBalanceSheet" class="mt-2">
                      Try Again
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'

const store = useStore()

const instance = getCurrentInstance()

const panel = ref([0])
const loading = ref(false)
const error = ref(null)
const balanceSheetData = ref(null)

const filters = ref({
  from_date: null,
  to_date: null,
})

const loadBalanceSheet = async () => {
  loading.value = true
  error.value = null
  
  try {
    const params = {
      from_date: filters.value.from_date,
      to_date: filters.value.to_date,
    }
    
    const response = await store.dispatch('getBalanceSheet', params)
    
    if (response.success) {
      balanceSheetData.value = response.data
    } else {
      error.value = response.message || 'Failed to load balance sheet'
    }
  } catch (err) {
    console.error('Error loading balance sheet:', err)
    error.value = err.response?.data?.message || 'Failed to load balance sheet'
  } finally {
    loading.value = false
  }
}

const resetFilters = () => {
  filters.value = {
    from_date: null,
    to_date: null,
  }
  balanceSheetData.value = null
  error.value = null
}

const formatDate = (date) => {
  if (!date) return ''
  try {
    const d = new Date(date)
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
  } catch {
    return ''
  }
}

const formatPreviousDate = (date) => {
  if (!date) return ''
  try {
    const d = new Date(date)
    const previousYear = new Date(d.getFullYear() - 1, d.getMonth(), d.getDate())
    return previousYear.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
  } catch {
    return ''
  }
}

const formatAmount = (amount) => {
  if (!amount) return '0.00'
  
  // Extract numeric value from strings like "1,500,000.00 Dr" or "800,000.00 Cr"
  const numericValue = parseFloat(amount.replace(/[^\d.-]/g, ''))
  
  if (isNaN(numericValue)) return '0.00'
  
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numericValue)
}

const exportToExcel = () => {
  if (!balanceSheetData.value) return
  
  const data = [
    ['STATEMENT OF FINANCIAL POSITION'],
    [`As at ${formatDate(filters.value.to_date)}`],
    [],
    ['Particulars', 'Notes', `Amount (${formatDate(filters.value.to_date)})`, `Amount (${formatPreviousDate(filters.value.to_date)})`],
    []
  ]

  // ASSETS SECTION
  data.push(['ASSETS', '', '', ''])
  
  // Add assets
  if (balanceSheetData.value.assets) {
    balanceSheetData.value.assets.forEach((assetGroup) => {
      data.push([assetGroup.name, '', assetGroup.current_balance || '0.00', assetGroup.previous_balance || '0.00'])
      
      if (assetGroup.account_head_groups) {
        assetGroup.account_head_groups.forEach((item) => {
          data.push([`  ${item.name}`, '', item.current_balance || '0.00', item.previous_balance || '0.00'])
        })
      }
    })
  }
  
  // Assets Total
  if (balanceSheetData.value.assets_total) {
    data.push([
      balanceSheetData.value.assets_total.name || 'Total Assets',
      '',
      balanceSheetData.value.assets_total.current_balance || '0.00',
      balanceSheetData.value.assets_total.previous_balance || '0.00'
    ])
  }
  
  data.push([])
  
  // EQUITY AND LIABILITIES SECTION
  data.push(['EQUITY AND LIABILITIES', '', '', ''])
  data.push(['EQUITY', '', '', ''])
  
  // Add equities
  if (balanceSheetData.value.equities) {
    balanceSheetData.value.equities.forEach((equity) => {
      data.push([`  ${equity.name}`, '', equity.current_balance || '0.00', equity.previous_balance || '0.00'])
    })
  }
  
  // Equities Total
  if (balanceSheetData.value.equities_total) {
    data.push([
      'Total Equity',
      '',
      balanceSheetData.value.equities_total.current_balance || '0.00',
      balanceSheetData.value.equities_total.previous_balance || '0.00'
    ])
  }
  
  data.push([])
  data.push(['LIABILITIES', '', '', ''])
  
  // Add liabilities
  if (balanceSheetData.value.liabilities) {
    balanceSheetData.value.liabilities.forEach((liabilityGroup) => {
      data.push([liabilityGroup.name, '', liabilityGroup.current_balance || '0.00', liabilityGroup.previous_balance || '0.00'])
      
      if (liabilityGroup.account_head_groups) {
        liabilityGroup.account_head_groups.forEach((item) => {
          data.push([`  ${item.name}`, '', item.current_balance || '0.00', item.previous_balance || '0.00'])
        })
      }
    })
  }
  
  // Liabilities Total
  if (balanceSheetData.value.liabilities_total) {
    data.push([
      'Total Liabilities',
      '',
      balanceSheetData.value.liabilities_total.current_balance || '0.00',
      balanceSheetData.value.liabilities_total.previous_balance || '0.00'
    ])
  }
  
  data.push([])
  
  // Grand Total
  if (balanceSheetData.value.equities_and_liabilities_total) {
    data.push([
      balanceSheetData.value.equities_and_liabilities_total.name || 'TOTAL EQUITY & LIABILITIES',
      '',
      balanceSheetData.value.equities_and_liabilities_total.current_balance || '0.00',
      balanceSheetData.value.equities_and_liabilities_total.previous_balance || '0.00'
    ])
  }

  instance.proxy.$exportTableToExcel(data, 'balance_sheet_' + instance.proxy.$fileDate())
}

const exportToPDF = () => {
  if (!balanceSheetData.value) return
  
  const columns = ['Particulars', 'Notes', `Current (${formatDate(filters.value.to_date)})`, `Previous (${formatPreviousDate(filters.value.to_date)})`]
  const rows = []

  // ASSETS SECTION
  rows.push(['ASSETS', '', '', ''])
  
  // Add assets
  if (balanceSheetData.value.assets) {
    balanceSheetData.value.assets.forEach((assetGroup) => {
      rows.push([assetGroup.name, '', assetGroup.current_balance || '0.00', assetGroup.previous_balance || '0.00'])
      
      if (assetGroup.account_head_groups) {
        assetGroup.account_head_groups.forEach((item) => {
          rows.push([`  ${item.name}`, '', item.current_balance || '0.00', item.previous_balance || '0.00'])
        })
      }
    })
  }
  
  // Assets Total
  if (balanceSheetData.value.assets_total) {
    rows.push([
      balanceSheetData.value.assets_total.name || 'Total Assets',
      '',
      balanceSheetData.value.assets_total.current_balance || '0.00',
      balanceSheetData.value.assets_total.previous_balance || '0.00'
    ])
  }
  
  rows.push(['', '', '', ''])
  
  // EQUITY AND LIABILITIES SECTION
  rows.push(['EQUITY AND LIABILITIES', '', '', ''])
  rows.push(['EQUITY', '', '', ''])
  
  // Add equities
  if (balanceSheetData.value.equities) {
    balanceSheetData.value.equities.forEach((equity) => {
      rows.push([`  ${equity.name}`, '', equity.current_balance || '0.00', equity.previous_balance || '0.00'])
    })
  }
  
  // Equities Total
  if (balanceSheetData.value.equities_total) {
    rows.push([
      'Total Equity',
      '',
      balanceSheetData.value.equities_total.current_balance || '0.00',
      balanceSheetData.value.equities_total.previous_balance || '0.00'
    ])
  }
  
  rows.push(['', '', '', ''])
  rows.push(['LIABILITIES', '', '', ''])
  
  // Add liabilities
  if (balanceSheetData.value.liabilities) {
    balanceSheetData.value.liabilities.forEach((liabilityGroup) => {
      rows.push([liabilityGroup.name, '', liabilityGroup.current_balance || '0.00', liabilityGroup.previous_balance || '0.00'])
      
      if (liabilityGroup.account_head_groups) {
        liabilityGroup.account_head_groups.forEach((item) => {
          rows.push([`  ${item.name}`, '', item.current_balance || '0.00', item.previous_balance || '0.00'])
        })
      }
    })
  }
  
  // Liabilities Total
  if (balanceSheetData.value.liabilities_total) {
    rows.push([
      'Total Liabilities',
      '',
      balanceSheetData.value.liabilities_total.current_balance || '0.00',
      balanceSheetData.value.liabilities_total.previous_balance || '0.00'
    ])
  }
  
  rows.push(['', '', '', ''])
  
  // Grand Total
  if (balanceSheetData.value.equities_and_liabilities_total) {
    rows.push([
      balanceSheetData.value.equities_and_liabilities_total.name || 'TOTAL EQUITY & LIABILITIES',
      '',
      balanceSheetData.value.equities_and_liabilities_total.current_balance || '0.00',
      balanceSheetData.value.equities_and_liabilities_total.previous_balance || '0.00'
    ])
  }

  instance.proxy.$exportTableToPDF(
    columns,
    rows,
    `STATEMENT OF FINANCIAL POSITION - As at ${formatDate(filters.value.to_date)}`,
    'balance_sheet_' + instance.proxy.$fileDate(),
    'portrait',
    {
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fontSize: 9, fillColor: [66, 139, 202], fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 80 },  // Particulars
        1: { cellWidth: 20 },  // Notes
        2: { cellWidth: 45, halign: 'right' },  // Current Period
        3: { cellWidth: 45, halign: 'right' }   // Previous Period
      },
      margin: { top: 40 }
    }
  )
}

onMounted(() => {
  // Set default dates (current year)
  const today = new Date()
  const yearStart = new Date(today.getFullYear(), 0, 1)
  
  filters.value.from_date = yearStart.toISOString().split('T')[0]
  filters.value.to_date = today.toISOString().split('T')[0]
})
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}

/* Exact Image Design - Individual Rectangular Boxes */
.balance-sheet-exact {
  font-family: 'Roboto', sans-serif;
  width: 100%;
  background-color: white;
}

/* Header Styles - Clean, No Borders */
.balance-header {
  display: flex;
  align-items: center;
  font-weight: bold;
  min-height: 40px;
  padding: 8px 0;
}

.balance-subheader {
  display: flex;
  align-items: center;
  font-weight: bold;
  min-height: 40px;
  padding: 8px 0;
}

.header-particulars {
  flex: 1;
  padding: 8px 12px;
  font-size: 14px;
  text-align: left;
}

.header-amounts {
  flex: 0 0 300px;
  padding: 8px 12px;
  font-size: 14px;
  text-align: center;
}

.header-amount-box {
  width: 120px;
  height: 30px;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  margin: 3px 12px;
  border-radius: 0;
  box-shadow: none;
  text-align: center;
  flex-shrink: 0;
}

/* Row Styles - Clean, No Borders */
.balance-row {
  display: flex;
  align-items: center;
  min-height: 35px;
  padding: 6px 0;
}

/* Particulars Text */
.particulars-text {
  flex: 1;
  padding: 8px 12px;
  font-size: 14px;
  text-align: left;
  background-color: white;
  font-weight: normal;
}

.indent {
  padding-left: 24px;
  font-size: 13px;
}

/* Section Headers - Bold */
.section-header {
  font-weight: bold;
}

.total-text {
  font-weight: bold;
}

.grand-total-text {
  font-weight: bold;
}

/* Individual Amount Boxes - No borders */
.amount-box {
  width: 120px;
  height: 30px;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: normal;
  margin: 3px 12px;
  border-radius: 0;
  box-shadow: none;
  text-align: center;
  flex-shrink: 0;
}

/* Empty boxes for section headers */
.amount-box-empty {
  width: 120px;
  height: 30px;
  background-color: white;
  margin: 3px 12px;
  flex-shrink: 0;
}

/* Special Row Styles */
.total-row {
  border-top: 1px solid #000;
  padding-top: 8px;
  margin-top: 6px;
}

.grand-total-row {
  border-top: 1px solid #000;
  border-bottom: 3px solid #000; /* Double underline for grand total */
  padding: 8px 0 6px 0;
  margin-top: 6px;
}

/* Special Box Styles */
.total-box {
  font-weight: bold;
}

.grand-total-box {
  font-weight: bold;
}

/* Separator Line */
.separator-line {
  width: 100%;
  height: 1px;
  background-color: #000;
  margin: 10px 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .balance-sheet-exact {
    font-size: 12px;
  }
  
  .header-amounts {
    flex: 0 0 240px;
  }
  
  .header-amount-box {
    width: 100px;
    height: 26px;
    font-size: 11px;
    margin: 3px 8px;
  }
  
  .amount-box {
    width: 100px;
    height: 26px;
    font-size: 11px;
    margin: 3px 8px;
  }
  
  .amount-box-empty {
    width: 100px;
    height: 26px;
    margin: 3px 8px;
  }
  
  .header-particulars,
  .particulars-text {
    padding: 6px 8px;
    font-size: 12px;
  }
}

/* Print Styles */
@media print {
  .balance-sheet-exact {
    font-size: 11px;
  }
  
  .header-amounts {
    flex: 0 0 260px;
  }
  
  .header-amount-box {
    width: 110px;
    height: 28px;
    font-size: 10px;
    margin: 3px 10px;
  }
  
  .amount-box {
    width: 110px;
    height: 28px;
    font-size: 10px;
    margin: 3px 10px;
  }
  
  .amount-box-empty {
    width: 110px;
    height: 28px;
    margin: 3px 10px;
  }
  
  .header-particulars,
  .particulars-text {
    padding: 4px 6px;
    font-size: 11px;
  }
  
  .separator-line {
    height: 1px;
  }
}

/* Hover Effects */
.balance-row:hover .particulars-text {
  background-color: #f8f9fa;
}

.balance-row:hover .amount-box {
  background-color: #f8f9fa;
}

.balance-subheader:hover .header-amount-box {
  background-color: #f8f9fa;
}

/* Ensure exact alignment like image */
.balance-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.particulars-text {
  flex: 1;
  min-width: 0;
}

.header-amount-box,
.amount-box,
.amount-box-empty {
  flex: 0 0 120px;
  margin: 3px 12px;
}

@media (max-width: 768px) {
  .header-amount-box,
  .amount-box,
  .amount-box-empty {
    flex: 0 0 100px;
    margin: 3px 8px;
  }
}

@media print {
  .header-amount-box,
  .amount-box,
  .amount-box-empty {
    flex: 0 0 110px;
    margin: 3px 10px;
  }
}
</style>
